
import Phaser from 'phaser';
import Player from './Player';
import GameScene from '../scenes/Game';
import Path from './Path';
import Node from './Node';

export class NodeDragHandler {

    // Constants: Distance configuration for troop movement
    private static readonly MIN_DRAG_DISTANCE: number = 40;
    private static readonly MAX_DRAG_DISTANCE: number = 120;

    // Constants: Arrow head
    private static readonly ARROW_HEAD_ORIGIN_X: number = 1;
    private static readonly ARROW_HEAD_ORIGIN_Y: number = 0.5;
    private static readonly ARROW_HEAD_INITIAL_ALPHA: number = 0;
    private static readonly ARROW_HEAD_DEPTH: number = 100;
    private static readonly ARROW_HEAD_SCALE_DIVISOR: number = 100;

    // Constants: Drag units display
    private static readonly DRAG_UNITS_DISPLAY_DEPTH: number = 1000;
    private static readonly DRAG_UNITS_DISPLAY_FONT_SIZE: number = 20;
    private static readonly DRAG_UNITS_DISPLAY_COLOR: string = '#ffff00';
    private static readonly DRAG_UNITS_DISPLAY_Y_OFFSET: number = -40;
    private static readonly DRAG_UNITS_DISPLAY_STROKE_COLOR: string = '#000000';
    private static readonly DRAG_UNITS_DISPLAY_STROKE_WIDTH: number = 4;
    private static readonly DRAG_UNITS_DISPLAY_FONT_SIZE_DRAGGING: number = 24;
 
    // Constants: Progress circle
    private static readonly PROGRESS_CIRCLE_RADIUS: number = 35;
    private static readonly PROGRESS_CIRCLE_COLOR: number = 0xffff00;
    private static readonly PROGRESS_CIRCLE_ALPHA: number = 0.7;
    private static readonly PROGRESS_CIRCLE_DEPTH: number = 100;

    // Constants: Arrow line
    private static readonly ARROW_LINE_ORIGIN_X: number = 0;
    private static readonly ARROW_LINE_ORIGIN_Y: number = 0.5;
    private static readonly ARROW_LINE_DEPTH: number = 99;
    private static readonly ARROW_LINE_MASK_INITIAL_WIDTH: number = 1;
    private static readonly ARROW_LINE_MASK_HEIGHT: number = 20;
    private static readonly ARROW_LINE_MASK_RECT_HEIGHT: number = 40;
    private static readonly LINE_MASK_OFFSET_X: number = 20;
    private static readonly LINE_MASK_OFFSET_Y: number = -20;
    private static readonly LINE_MASK_END_OFFSET: number = 50;

    private scene: GameScene;
    private node: Node;
    private dragCounterAtDragStart = 0;
    private dragStartX = 0;
    private dragStartY = 0;
    private dragArrowHead?: Phaser.GameObjects.Image;
    private dragArrowLine?: Phaser.GameObjects.Image;
    private dragArrowLineMask?: Phaser.Display.Masks.GeometryMask;
    private dragUnitsDisplay?: Phaser.GameObjects.Text;
    private dragUnitsDisplayBg?: Phaser.GameObjects.Image;
    private dragProgressCircle?: Phaser.GameObjects.Graphics;
    private originalX: number;
    private originalY: number;

    constructor(scene: GameScene, node: Node, originalX: number, originalY: number) {
        this.scene = scene;
        this.node = node;
        this.originalX = originalX;
        this.originalY = originalY;

        this.setDraggableBehaviour();
    }

    private setDraggableBehaviour() {
        this.scene.input.setDraggable(this.node, true);

        this.node.on('dragstart', (_: any, dragX: number, dragY: number) => {
            this.dragCounterAtDragStart = this.node.counter; // Access node.counter directly
            if (!this.isDraggable()) return;

            this.node.setVisibleText(false); // Delegate to NodeDisplay for text visibility

            this.dragStartX = dragX;
            this.dragStartY = dragY;

            this.createArrowHead();
            this.createArrowLine();
            this.createDragUnitsDisplay();

            this.getRelatedPaths().forEach((path: Path) => {
                path.bringToFront();
                path.enableHover();
            });
        });

        this.node.on('drag', (_: any, dragX: number, dragY: number) => {
            if (!this.isDraggable()) return;

            this.moveArrowHead(dragX, dragY);
            this.moveArrowLine();
            this.updateDragUnitsDisplay();
        });

        this.node.on('drop', (_: any, dropObject: any) => {
            if (!this.isDraggable()) return;

            this.node.x = this.originalX;
            this.node.y = this.originalY;

            const movementPercentage = this.calculateMovementPercentageFromDistance();

            const pathReference = (dropObject as any)?.pathReference;
  
            if (pathReference) {
                let targetNode: any;

                if (pathReference.origenNode === this.node || (pathReference.origenNode && pathReference.origenNode.id === this.node.id)) {
                    targetNode = pathReference.destineNode;
                } else if (pathReference.destineNode === this.node || (pathReference.destineNode && pathReference.destineNode.id === this.node.id)) {
                    targetNode = pathReference.origenNode;
                } else {
                    console.warn('Path drop: neither path end matches the dragged node — aborting movement', { pathReference, draggedNode: this.node });
                    return;
                }

                this.scene.getPaths().startPathMovement(this.node, targetNode, movementPercentage);
                pathReference.unhighlight();
            } else if (dropObject instanceof Node) {
                this.scene.getPaths().startPathMovement(this.node, dropObject, movementPercentage);
            }
        });

        this.node.on('dragend', (_pointer: any, _dragX: number, _dragY: number) => {
            this.node.setVisibleText(true);

            this.dragArrowHead?.destroy();
            this.dragUnitsDisplay?.destroy();
            this.dragUnitsDisplayBg?.destroy();
            this.dragProgressCircle?.destroy();

            if (this.dragArrowLineMask) {
                if (this.dragArrowLineMask.geometryMask) {
                    const maskGraphics = this.dragArrowLineMask.geometryMask as Phaser.GameObjects.Graphics;
                    maskGraphics.destroy();
                }
                
                this.dragArrowLineMask.destroy();
            }
            this.dragArrowLine?.destroy();

            this.getRelatedPaths().forEach((path: Path) => {
                path.sendToBack();
                path.disableHover();
            });
        });
    }

    private isDraggable(): boolean {
        return (
            (this.node.player.type === Player.humanType || this.scene.getBootloaderScene().globals.debugMode)
            && this.dragCounterAtDragStart > 0
        );
    }

    private getRelatedPaths(): Path[] {
        return this.scene.getPaths().all().filter(path => path.origenNode === this.node || path.destineNode === this.node);
    }

    private calculateMovementPercentageFromDistance(): number {
        if (!this.dragArrowHead || !this.dragArrowHead.active) return 0;

        const distance = Phaser.Math.Distance.Between(
            this.originalX,
            this.originalY,
            this.dragArrowHead.x,
            this.dragArrowHead.y
        );

        const clampedDistance = Phaser.Math.Clamp(
            distance,
            NodeDragHandler.MIN_DRAG_DISTANCE,
            NodeDragHandler.MAX_DRAG_DISTANCE
        );

        const percentage = (clampedDistance - NodeDragHandler.MIN_DRAG_DISTANCE) / (NodeDragHandler.MAX_DRAG_DISTANCE - NodeDragHandler.MIN_DRAG_DISTANCE);

        return Phaser.Math.Clamp(percentage, 0, 1);
    }

    public calculateUnitsToMove(movementPercentage: number): number {
        if (movementPercentage <= 0) return 0;
        const units = Math.round(this.node.counter * (movementPercentage));

        return Math.min(units, this.node.counter);
    }

    private createArrowHead() {
        this.dragArrowHead = this.scene.add.image(this.originalX, this.originalY, 'arrow_head').setInteractive({ dropZone: true });
        this.dragArrowHead.disableInteractive();
        this.dragArrowHead.setOrigin(NodeDragHandler.ARROW_HEAD_ORIGIN_X, NodeDragHandler.ARROW_HEAD_ORIGIN_Y);
        this.dragArrowHead.setAlpha(NodeDragHandler.ARROW_HEAD_INITIAL_ALPHA);
        const realArrowWidth = ((this.dragArrowHead.width * this.dragArrowHead.scale) / 2);
        this.dragArrowHead.setX(this.dragArrowHead.x + this.dragStartX - realArrowWidth);
        this.dragArrowHead.setY(this.dragArrowHead.y + this.dragStartY);
        this.dragArrowHead.setDepth(NodeDragHandler.ARROW_HEAD_DEPTH);
    }

    private createDragUnitsDisplay(): void {
        this.dragUnitsDisplayBg = this.scene.add.image(
            this.originalX,
            this.originalY,
            'arrow_circle_bg'
        ).setDepth(NodeDragHandler.DRAG_UNITS_DISPLAY_DEPTH);

        this.dragUnitsDisplay = this.scene.add.text(
            this.originalX,
            this.originalY,
            '0',
            {
                fontFamily: 'arial',
                color: NodeDragHandler.DRAG_UNITS_DISPLAY_COLOR,
                fontSize: NodeDragHandler.DRAG_UNITS_DISPLAY_FONT_SIZE,
                fontStyle: 'bold'
            }
        ).setOrigin(0.5)
            .setDepth(NodeDragHandler.DRAG_UNITS_DISPLAY_DEPTH);

        this.dragProgressCircle = this.scene.add.graphics().setDepth(NodeDragHandler.PROGRESS_CIRCLE_DEPTH);
    }

    private createArrowLine() {
        this.dragArrowLine = this.scene.add.image(this.originalX, this.originalY, 'arrow_line')
            .setOrigin(NodeDragHandler.ARROW_LINE_ORIGIN_X, NodeDragHandler.ARROW_LINE_ORIGIN_Y)
            .setDepth(NodeDragHandler.ARROW_LINE_DEPTH);

        const maskGraphics = this.scene.add.graphics();
        maskGraphics.setPosition(this.originalX, this.originalY);
        maskGraphics.fillStyle(0xffffff, 0);
        maskGraphics.fillRect(0, 0, NodeDragHandler.ARROW_LINE_MASK_INITIAL_WIDTH, NodeDragHandler.ARROW_LINE_MASK_HEIGHT);
        this.dragArrowLineMask = new Phaser.Display.Masks.GeometryMask(this.scene, maskGraphics);
        this.dragArrowLine.setMask(this.dragArrowLineMask);
    }

    private moveArrowHead(dragX: number, dragY: number): void {
        if (this.dragArrowHead) {
            const distance = Phaser.Math.Distance.Between(
                this.originalX,
                this.originalY,
                this.dragArrowHead.x,
                this.dragArrowHead.y
            );

            this.dragArrowHead.x = dragX + this.dragStartX;
            this.dragArrowHead.y = dragY + this.dragStartY;
            this.dragArrowHead.setScale(Math.min(distance / NodeDragHandler.ARROW_HEAD_SCALE_DIVISOR, 1));
            this.dragArrowHead.setAlpha(1);
            this.dragArrowHead.setAngle(
                Phaser.Math.RadToDeg(
                    Phaser.Math.Angle.Between(this.originalX, this.originalY, this.dragArrowHead.x, this.dragArrowHead.y)
                )
            );
        }
    }

    private moveArrowLine() {
        if (this.dragArrowLine && this.dragArrowHead && this.dragArrowLineMask) {
            const dx = this.dragArrowHead.x - this.originalX;
            const dy = this.dragArrowHead.y - this.originalY;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const angle = Math.atan2(dy, dx);

            this.dragArrowLine.setRotation(angle);

            const maskGraphics = this.dragArrowLineMask.geometryMask as Phaser.GameObjects.Graphics;
            maskGraphics.setPosition(this.originalX, this.originalY);
            maskGraphics.setRotation(angle);
            maskGraphics.clear();
            maskGraphics.fillStyle(0xffffff, 0);
            maskGraphics.fillRect(NodeDragHandler.LINE_MASK_OFFSET_X, NodeDragHandler.LINE_MASK_OFFSET_Y, distance - NodeDragHandler.LINE_MASK_END_OFFSET, NodeDragHandler.ARROW_LINE_MASK_RECT_HEIGHT);
        }
    }

    private updateDragUnitsDisplay(): void {
        if (!this.dragArrowHead || !this.dragArrowHead.active) return;

        const movementPercentage = this.calculateMovementPercentageFromDistance();
        const unitsToMove = this.calculateUnitsToMove(movementPercentage);

        if (!this.dragUnitsDisplay || !this.dragUnitsDisplay.active) {
            this.dragUnitsDisplay?.destroy();

            this.dragUnitsDisplay = this.scene.add.text(
                this.dragArrowHead.x,
                this.dragArrowHead.y + NodeDragHandler.DRAG_UNITS_DISPLAY_Y_OFFSET,
                unitsToMove.toString(),
                {
                    fontFamily: 'arial',
                    color: NodeDragHandler.DRAG_UNITS_DISPLAY_COLOR,
                    fontSize: NodeDragHandler.DRAG_UNITS_DISPLAY_FONT_SIZE_DRAGGING,
                    fontStyle: 'bold'
                }
            )
                .setStroke(NodeDragHandler.DRAG_UNITS_DISPLAY_STROKE_COLOR, NodeDragHandler.DRAG_UNITS_DISPLAY_STROKE_WIDTH)
                .setOrigin(0.5)
                .setDepth(NodeDragHandler.DRAG_UNITS_DISPLAY_DEPTH);
        } else {
            this.dragUnitsDisplay.setText(unitsToMove.toString());
        }

        this.updateProgressCircle(movementPercentage);
    }

    private updateProgressCircle(percentage: number): void {
        if (this.dragProgressCircle) {
            this.dragProgressCircle.clear();
            if (percentage > 0) {
                const centerX = this.originalX;
                const centerY = this.originalY;
                const radius = NodeDragHandler.PROGRESS_CIRCLE_RADIUS;
                const startAngle = -Math.PI / 2;
                const endAngle = startAngle + (percentage * 2 * Math.PI);
                this.dragProgressCircle.fillStyle(NodeDragHandler.PROGRESS_CIRCLE_COLOR, NodeDragHandler.PROGRESS_CIRCLE_ALPHA);
                this.dragProgressCircle.beginPath();
                this.dragProgressCircle.moveTo(centerX, centerY);
                this.dragProgressCircle.arc(centerX, centerY, radius, startAngle, endAngle);
                this.dragProgressCircle.closePath();
                this.dragProgressCircle.fillPath();
            }
        }
    }
}
